import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getPlayerId } from "../utils";
import { apiFetch } from "../apiBase";

export interface UserProfile {
  // Identificação
  odysId: string;
  username: string | null;
  displayName: string | null;
  email: string | null;
  
  // Dados pessoais (opcionais)
  birthDate: string | null; // ISO 8601 format
  bio: string | null;
  
  // Avatar e personalização
  selectedAvatar: string; // ID do personagem da história
  customAvatarUrl: string | null; // URL de avatar personalizado (OAuth)
  
  // Conexões OAuth
  googleId: string | null;
  appleId: string | null;
  githubId: string | null;
  discordId: string | null;
  
  // Metadata
  createdAt: number;
  lastUpdatedAt: number;
}

interface ProfileState {
  profile: UserProfile;
  availableAvatars: string[]; // IDs dos avatares desbloqueados
  isLoaded: boolean;
  
  // Ações
  updateProfile: (updates: Partial<UserProfile>) => void;
  setAvatar: (avatarId: string) => void;
  addAvailableAvatar: (avatarId: string) => void;
  linkOAuthProvider: (provider: 'google' | 'apple' | 'github' | 'discord', providerId: string) => void;
  
  // Server sync
  loadFromServer: () => Promise<void>;
  saveToServer: () => Promise<void>;
}

const syncToServer = async (profile: UserProfile) => {
  try {
    await apiFetch("/api/profile", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(profile),
    });
  } catch (error) {
    console.error("Failed to sync profile to server:", error);
  }
};

export const useProfile = create<ProfileState>()(
  persist(
    (set, get) => ({
      profile: {
        odysId: getPlayerId(),
        username: null,
        displayName: null,
        email: null,
        birthDate: null,
        bio: null,
        selectedAvatar: "arthur", // Avatar padrão
        customAvatarUrl: null,
        googleId: null,
        appleId: null,
        githubId: null,
        discordId: null,
        createdAt: Date.now(),
        lastUpdatedAt: Date.now(),
      },
      availableAvatars: ["arthur"], // Arthur está sempre disponível
      isLoaded: false,
      
      updateProfile: (updates) => {
        set((state) => ({
          profile: {
            ...state.profile,
            ...updates,
            lastUpdatedAt: Date.now(),
          },
        }));
        
        // Sync to server
        const { profile } = get();
        syncToServer(profile);
      },
      
      setAvatar: (avatarId) => {
        const { availableAvatars } = get();
        
        if (!availableAvatars.includes(avatarId)) {
          console.warn(`Avatar ${avatarId} is not unlocked`);
          return;
        }
        
        set((state) => ({
          profile: {
            ...state.profile,
            selectedAvatar: avatarId,
            lastUpdatedAt: Date.now(),
          },
        }));
        
        const { profile } = get();
        syncToServer(profile);
      },
      
      addAvailableAvatar: (avatarId) => {
        set((state) => {
          if (state.availableAvatars.includes(avatarId)) {
            return state;
          }
          
          return {
            availableAvatars: [...state.availableAvatars, avatarId],
          };
        });
      },
      
      linkOAuthProvider: (provider, providerId) => {
        const updates: Partial<UserProfile> = {
          lastUpdatedAt: Date.now(),
        };
        
        switch (provider) {
          case 'google':
            updates.googleId = providerId;
            break;
          case 'apple':
            updates.appleId = providerId;
            break;
          case 'github':
            updates.githubId = providerId;
            break;
          case 'discord':
            updates.discordId = providerId;
            break;
        }
        
        set((state) => ({
          profile: {
            ...state.profile,
            ...updates,
          },
        }));
        
        const { profile } = get();
        syncToServer(profile);
      },
      
      loadFromServer: async () => {
        try {
          const playerId = getPlayerId();
          const response = await apiFetch(`/api/profile?odysId=${playerId}`);
          
          if (response.ok) {
            const data = await response.json();
            
            set({
              profile: {
                ...get().profile,
                ...data.profile,
              },
              availableAvatars: data.availableAvatars || ["arthur"],
              isLoaded: true,
            });
          } else {
            // Profile not found, create new one
            set({ isLoaded: true });
            await get().saveToServer();
          }
        } catch (error) {
          console.error("Failed to load profile from server:", error);
          set({ isLoaded: true });
        }
      },
      
      saveToServer: async () => {
        const { profile } = get();
        await syncToServer(profile);
      },
    }),
    {
      name: "rocketanks-profile",
      version: 1,
    }
  )
);
